import {isFeatureEnabled} from 'feature-flags';
import {DataStatus, Label, MultiChoice, TextInput} from 'platform/components';
import {Grid, Show, VStack} from 'platform/foundation';

import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';

import {isNotNilOrEmpty, suffixTestId, TestIdProps} from 'shared/web';

import {KwToPsMultiplier} from '../../../../../../constants/KwToPsMultiplier';
import {sourcingFilterConstants} from '../../../../../../constants/sourcingFilterConstants';
import {useApiEnums} from '../../../../../../hooks/useApiEnums';
import {useSourcingType} from '../../../../../../hooks/useSourcingType';
import {VehicleCategoryFilter} from '../../../../types/VehicleCategoryFilter';
import {areVanDimensionFiltersVisible} from '../../../../utils/areVanDimensionFiltersVisible';
import {getAppliedVehicleCategory} from '../../../../utils/getAppliedVehicleCategory';
import {getBodyTypeOptions} from '../../../../utils/getBodyTypeOptions';
import {FilterField} from '../../../FilterEditor/components/FilterField';
import {useFilters} from '../../../FiltersContext/FiltersContext';
import {SourcingSlider} from '../SourcingSlider/SourcingSlider';
import {ColorPicker} from './components/ColorPicker/ColorPicker';
import {TrimLevelFilter} from './components/TrimLevelFilter';
import {filterSpecificationOptions} from './utils/filterSpecificationOptions';

const currentYear = new Date().getFullYear();

export function FilterSpecification(props: TestIdProps & {isCompact?: boolean}) {
  const {filters, onUpdateFilters} = useFilters();
  const {getEnum, isLoading} = useApiEnums();
  const sourcingType = useSourcingType();

  const PStoKW = (PS: number) => Math.round(PS / KwToPsMultiplier);
  const KWtoPS = (KW: number) => Math.round(KW * KwToPsMultiplier);
  const normalizePowerValue = (value: number) => (filters.isPowerInKW ? value : KWtoPS(value));
  const normalizePowerRange = ([min, max]: [number, number]): [number, number] =>
    filters.isPowerInKW ? [min, max] : [KWtoPS(min), KWtoPS(max)];
  const reverseNormalizePowerRange = ([min, max]: [number, number]): [number, number] =>
    filters.isPowerInKW ? [min, max] : [PStoKW(min), PStoKW(max)];
  const normalizePowerMarks = (marks: {value: number}[]) =>
    filters.isPowerInKW ? marks : marks.map((mark) => ({value: KWtoPS(mark.value)}));

  const fuelTypes = getEnum('fuel_type');
  const transmissions = getEnum('transmission').filter(
    (item) => !filterSpecificationOptions.ignoredTransmissions.includes(item.key)
  );
  const isVanFiltersEnabled = isFeatureEnabled(featureFlags.SOURCING_VAN_FILTERS);
  const bodyStyles = getEnum('car_style');
  // With the vehicle category feature the body styles are grouped (cars / vans) and narrowed by the selected category.
  // Without it we keep the flat list, which is what the API returned before vans were added.
  const bodyTypes = isVanFiltersEnabled
    ? getBodyTypeOptions(
        bodyStyles,
        getAppliedVehicleCategory(filters.vehicleCategory, sourcingType),
        {
          [VehicleCategoryFilter.CAR]: i18n.t('entity.sourcing.filters.labels.vehicleCategoryCar'),
          [VehicleCategoryFilter.VAN]: i18n.t('entity.sourcing.filters.labels.vehicleCategoryVan'),
        }
      )
    : bodyStyles;
  const isVanDimensionFilterVisible = areVanDimensionFiltersVisible(
    filters.vehicleCategory,
    sourcingType,
    isVanFiltersEnabled
  );
  const vehicleLengthCategories = getEnum('dimensions_length');
  const vehicleHeightCategories = getEnum('dimensions_height');
  const doorCounts = getEnum('door_count').filter((item) =>
    filterSpecificationOptions.allowedDoorCount.includes(item.key)
  );
  const drives = getEnum('drive').filter(
    (item) => !filterSpecificationOptions.ignoredDrives.includes(item.key)
  );
  const bodyColors = getEnum('body_color');
  const interiorMaterials = getEnum('interior_material');

  return (
    <DataStatus isLoading={isLoading}>
      <VStack spacing={8} data-testid={suffixTestId('filtersSpecification', props)}>
        <Grid columns={props.isCompact ? 1 : [1, 2, 4]} spacing={8}>
          <FilterField name="fuelType">
            <MultiChoice
              noOptionsMessage={() => i18n.t('general.labels.noOptions')}
              menuInPortal={props.isCompact}
              label={i18n.t('entity.vehicle.labels.fuelType')}
              options={fuelTypes}
              value={filters.fuelType ?? null}
              onChange={(option) =>
                onUpdateFilters('fuelType', isNotNilOrEmpty(option) ? option : undefined)
              }
              data-testid={suffixTestId('fuelType', props)}
            />
          </FilterField>

          <FilterField name="transmission">
            <MultiChoice
              noOptionsMessage={() => i18n.t('general.labels.noOptions')}
              menuInPortal={props.isCompact}
              label={i18n.t('entity.vehicle.labels.transmission')}
              options={transmissions}
              value={filters.transmission ?? null}
              onChange={(option) =>
                onUpdateFilters('transmission', isNotNilOrEmpty(option) ? option : undefined)
              }
              data-testid={suffixTestId('transmission', props)}
            />
          </FilterField>

          <FilterField name="bodyType">
            <MultiChoice
              noOptionsMessage={() => i18n.t('general.labels.noOptions')}
              menuInPortal={props.isCompact}
              label={i18n.t('entity.vehicle.labels.bodyType')}
              options={bodyTypes}
              value={filters.bodyType ?? null}
              onChange={(option) =>
                onUpdateFilters('bodyType', isNotNilOrEmpty(option) ? option : undefined)
              }
              data-testid={suffixTestId('bodyType', props)}
            />
          </FilterField>

          <Show when={isVanDimensionFilterVisible}>
            <FilterField name="vehicleLengthCategory">
              <MultiChoice
                noOptionsMessage={() => i18n.t('general.labels.noOptions')}
                menuInPortal={props.isCompact}
                label={i18n.t('entity.vehicle.labels.vehicleLength')}
                tooltip={i18n.t('entity.sourcing.filters.labels.vanDimensionsTooltip')}
                options={vehicleLengthCategories}
                value={filters.vehicleLengthCategory ?? null}
                onChange={(option) =>
                  onUpdateFilters(
                    'vehicleLengthCategory',
                    isNotNilOrEmpty(option) ? option : undefined
                  )
                }
                data-testid={suffixTestId('vehicleLengthCategory', props)}
              />
            </FilterField>

            <FilterField name="vehicleHeightCategory">
              <MultiChoice
                noOptionsMessage={() => i18n.t('general.labels.noOptions')}
                menuInPortal={props.isCompact}
                label={i18n.t('entity.vehicle.labels.vehicleHeight')}
                tooltip={i18n.t('entity.sourcing.filters.labels.vanDimensionsTooltip')}
                options={vehicleHeightCategories}
                value={filters.vehicleHeightCategory ?? null}
                onChange={(option) =>
                  onUpdateFilters(
                    'vehicleHeightCategory',
                    isNotNilOrEmpty(option) ? option : undefined
                  )
                }
                data-testid={suffixTestId('vehicleHeightCategory', props)}
              />
            </FilterField>
          </Show>

          <FilterField name="doorCount">
            <MultiChoice
              noOptionsMessage={() => i18n.t('general.labels.noOptions')}
              menuInPortal={props.isCompact}
              label={i18n.t('entity.vehicle.labels.numberOfDoors')}
              options={doorCounts}
              value={filters.doorCount ?? null}
              onChange={(option) =>
                onUpdateFilters('doorCount', isNotNilOrEmpty(option) ? option : undefined)
              }
              data-testid={suffixTestId('doorCount', props)}
            />
          </FilterField>

          <FilterField name="drive">
            <MultiChoice
              noOptionsMessage={() => i18n.t('general.labels.noOptions')}
              menuInPortal={props.isCompact}
              label={i18n.t('entity.vehicle.labels.driveType')}
              options={drives}
              value={filters.drive ?? null}
              onChange={(option) =>
                onUpdateFilters('drive', isNotNilOrEmpty(option) ? option : undefined)
              }
              data-testid={suffixTestId('driveType', props)}
            />
          </FilterField>

          <FilterField name="adTitle">
            <TextInput
              label={i18n.t('entity.vehicle.labels.adTitle')}
              value={filters.adTitle ?? ''}
              onChange={(value) => onUpdateFilters('adTitle', value || undefined)}
              data-testid={suffixTestId('adTitle', props)}
            />
          </FilterField>
          <Show whenFeatureEnabled={featureFlags.SOURCING_FILTER_REDESIGN}>
            <FilterField name="trimLevels">
              <TrimLevelFilter isCompact={props.isCompact} data-testid={props['data-testid']} />
            </FilterField>
          </Show>
        </Grid>

        <Grid columns={props.isCompact ? 1 : [1, 2, 4]} spacing={8}>
          <FilterField name="mileageRange">
            <SourcingSlider
              label={i18n.t('entity.vehicle.labels.mileageRange')}
              min={sourcingFilterConstants.MILEAGE_RANGE_MIN}
              max={sourcingFilterConstants.MILEAGE_RANGE_MAX}
              marks={sourcingFilterConstants.MILEAGE_RANGE_MARKS}
              hasMaxPlus
              value={filters.mileageRange}
              onChange={(value) => onUpdateFilters('mileageRange', value)}
              data-testid={suffixTestId('mileageRange', props)}
            />
          </FilterField>

          <FilterField name="year">
            <SourcingSlider
              label={i18n.t('entity.vehicle.labels.year')}
              isFormatNumbersDisabled
              min={sourcingFilterConstants.YEAR_LIMIT_MIN}
              max={currentYear}
              value={filters.year}
              onChange={(value) => onUpdateFilters('year', value)}
              data-testid={suffixTestId('year', props)}
            />
          </FilterField>

          <FilterField name="power">
            <SourcingSlider
              label={i18n.t('entity.vehicle.labels.power')}
              min={normalizePowerValue(sourcingFilterConstants.POWER_LIMIT_MIN)}
              marks={normalizePowerMarks(sourcingFilterConstants.POWER_MARKS)}
              max={normalizePowerValue(sourcingFilterConstants.POWER_LIMIT_MAX)}
              hasMaxPlus
              value={normalizePowerRange(
                filters.power ?? [
                  sourcingFilterConstants.POWER_LIMIT_MIN,
                  sourcingFilterConstants.POWER_LIMIT_MAX,
                ]
              )}
              onChange={(value) => onUpdateFilters('power', reverseNormalizePowerRange(value))}
              segmentOptions={['kW', 'PS']}
              segmentValue={filters.isPowerInKW ? 'kW' : 'PS'}
              onSegmentChange={(unit) => onUpdateFilters('isPowerInKW', unit === 'kW')}
              data-testid={suffixTestId('power', props)}
            />
          </FilterField>

          <FilterField name="cubicCapacity">
            <SourcingSlider
              label={i18n.t('entity.vehicle.labels.cubicCapacity')}
              min={sourcingFilterConstants.CUBIC_CAPACITY_LIMITS[0]}
              max={sourcingFilterConstants.CUBIC_CAPACITY_LIMITS[1]}
              marks={sourcingFilterConstants.CUBIC_CAPACITY_MARKS}
              hasMaxPlus
              value={filters.cubicCapacity}
              onChange={(value) => onUpdateFilters('cubicCapacity', value)}
              data-testid={suffixTestId('cubicCapacity', props)}
            />
          </FilterField>

          <FilterField name="numberOfSeats">
            <SourcingSlider
              label={i18n.t('entity.vehicle.labels.numberOfSeats')}
              min={sourcingFilterConstants.NUMBER_OF_SEATS_MIN}
              max={sourcingFilterConstants.NUMBER_OF_SEATS_MAX}
              hasMaxPlus
              value={filters.numberOfSeats}
              onChange={(value) => onUpdateFilters('numberOfSeats', value)}
              data-testid={suffixTestId('numberOfSeats', props)}
            />
          </FilterField>

          <FilterField name="featureScore">
            <SourcingSlider
              label={i18n.t('entity.vehicle.labels.rankEquipment')}
              step={20}
              min={sourcingFilterConstants.FEATURE_SCORE_MIN}
              max={sourcingFilterConstants.FEATURE_SCORE_MAX}
              value={filters.featureScore}
              onChange={(value) => onUpdateFilters('featureScore', value)}
              data-testid={suffixTestId('rankEquipment', props)}
            />
          </FilterField>

          <FilterField name="selectedColors">
            <VStack height="100%" justify="space-between">
              <Label>{i18n.t('general.labels.colors')}</Label>
              <ColorPicker
                colors={bodyColors}
                selectedColors={filters.selectedColors}
                onChange={(colors) => onUpdateFilters('selectedColors', colors)}
              />
            </VStack>
          </FilterField>

          <Show when={isFeatureEnabled(featureFlags.SOURCING_INTERIOR_MATERIAL_FILTER)}>
            <FilterField name="interiorMaterial">
              <MultiChoice
                noOptionsMessage={() => i18n.t('general.labels.noOptions')}
                menuInPortal={props.isCompact}
                label={i18n.t('entity.vehicle.labels.interiorMaterial')}
                options={interiorMaterials}
                value={filters.interiorMaterial ?? null}
                onChange={(option) =>
                  onUpdateFilters('interiorMaterial', isNotNilOrEmpty(option) ? option : undefined)
                }
                data-testid={suffixTestId('interiorMaterial', props)}
              />
            </FilterField>
          </Show>
        </Grid>
      </VStack>
    </DataStatus>
  );
}
